import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, ReactElement } from 'react';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { LARGE_SCREEN_BREAKPOINT, MAIN_TILE_ID } from './constants';
import {
    TileErrorBoundaryProvider,
    TileWidthProvider,
    TilingEngineProvider,
    useTilingEngineContext,
} from './TilingEngineContext';
import { TilingEngineMain } from './TilingEngineMain';
import { TilingEnginePanel } from './TilingEnginePanel';
import { TilingEnginePortal } from './TilingEnginePortal';
import { TilingEngineRightDock } from './TilingEngineRightDock';
import type {
    TileKind,
    TilingEngineMainProps,
    TilingEnginePanelProps,
    TilingEngineRightDockProps,
    TilingEngineRootProps,
} from './types';
import { useDragToScroll } from './use-drag-to-scroll';
import { useTileWidths } from './use-tile-widths';

type TileDescriptor = {
    readonly id: string;
    readonly kind?: TileKind;
    readonly minWidth?: string;
    readonly maxWidth?: string;
    readonly width?: string;
};

const TilingEngineRootInner: FunctionComponent<TilingEngineRootProps> = ({
    autoScrollOnChange = 'newest',
    children,
    className,
    firstPanelWidth,
    offsetTop,
    style,
    tileErrorBoundary,
}) => {
    const { allTileIdsRef, containerRef, notify, registeredTiles, scrollCoordinator, setTileOrder, tileOrder } =
        useTilingEngineContext();
    const { containerProps, isDragging } = useDragToScroll();
    const isLargeScreen = useMediaQuery(LARGE_SCREEN_BREAKPOINT);

    const childArray = React.Children.toArray(children);

    const mainChild = childArray.find(
        (child): child is ReactElement<TilingEngineMainProps> =>
            React.isValidElement(child) && child.type === TilingEngineMain,
    );

    const panelChildren = childArray.filter(
        (child): child is ReactElement<TilingEnginePanelProps> =>
            React.isValidElement(child) && child.type === TilingEnginePanel,
    );

    const dockChildren = childArray.filter(
        (child): child is ReactElement<TilingEngineRightDockProps> =>
            React.isValidElement(child) && child.type === TilingEngineRightDock,
    );

    const panelKey = panelChildren
        .map((p) => `${p.props.id}:${p.props.width}:${p.props.minWidth}:${p.props.maxWidth}`)
        .join('|');

    const dockKey = dockChildren
        .map((d) => `${d.props.id}:${d.props.width}:${d.props.minWidth}:${d.props.maxWidth}`)
        .join('|');

    // biome-ignore lint/correctness/useExhaustiveDependencies: panelKey/dockKey are stable serializations of the prop sets — used instead of unstable array references
    const tileDescriptors = useMemo<readonly TileDescriptor[]>(() => {
        const mainDescriptor: TileDescriptor = {
            id: MAIN_TILE_ID,
            maxWidth: mainChild?.props.maxWidth,
            minWidth: mainChild?.props.minWidth,
        };

        const declarativePanels: readonly TileDescriptor[] = panelChildren.map((panel) => ({
            id: panel.props.id,
            kind: 'panel',
            maxWidth: panel.props.maxWidth,
            minWidth: panel.props.minWidth,
            width: panel.props.width,
        }));

        const imperativeTiles: readonly TileDescriptor[] = registeredTiles.map((tile) => ({
            id: tile.id,
            kind: tile.kind ?? 'panel',
            maxWidth: tile.maxWidth,
            minWidth: tile.minWidth,
            width: tile.width,
        }));

        const declarativeDocks: readonly TileDescriptor[] = dockChildren.map((dock) => ({
            id: dock.props.id,
            kind: 'dock',
            maxWidth: dock.props.maxWidth,
            minWidth: dock.props.minWidth,
            width: dock.props.width,
        }));

        return [mainDescriptor, ...declarativePanels, ...imperativeTiles, ...declarativeDocks];
    }, [mainChild?.props.maxWidth, mainChild?.props.minWidth, panelKey, dockKey, registeredTiles]);

    const tileWidths = useTileWidths(tileDescriptors, isLargeScreen, firstPanelWidth);

    const tileKindByIdRef = useRef<Map<string, TileKind>>(new Map());

    useLayoutEffect(() => {
        const order = tileDescriptors.map((t) => t.id);
        allTileIdsRef.current = order;
        setTileOrder(order);

        const kindMap = new Map<string, TileKind>();
        for (const descriptor of tileDescriptors) {
            if (descriptor.kind !== undefined) {
                kindMap.set(descriptor.id, descriptor.kind);
            }
        }
        tileKindByIdRef.current = kindMap;
    }, [tileDescriptors, allTileIdsRef, setTileOrder]);

    const didEmitInitialSettled = useRef(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: refs should not go to depenencies.
    useEffect(() => {
        if (didEmitInitialSettled.current) {
            return;
        }

        didEmitInitialSettled.current = true;

        if (panelChildren.length > 0 || registeredTiles.length > 0 || dockChildren.length > 0) {
            notify({
                type: 'layoutSettled',
                tileIds: allTileIdsRef.current,
            });
        }
    }, []);

    const previousTileOrderRef = useRef<readonly string[]>(tileOrder);
    const isInitialOrderRef = useRef(true);

    // When a sticky-right dock is present, the correct "end" position for the
    // newest tile is the surface's natural rightmost rest (`scrollWidth -
    // clientWidth`). `scrollToTile(id, 'end')` can't compute that landing site
    // because:
    //   - sticky tiles never move with scrollLeft, so their getBoundingClientRect
    //     is identical at every scroll position (the dock-id case is a no-op);
    //   - for a non-dock newest tile, the formula doesn't see the container's
    //     padding-right or the flex `gap-m` between panel and dock, so the
    //     tile lands flush against (or behind) the dock.
    // `scrollToEnd` clamps to scrollLeftMax, which the browser computes
    // including all paddings + gaps — exactly the visual layout we want.
    const scrollToRightEdgeFor = (id: string): void => {
        const hasDock = Array.from(tileKindByIdRef.current.values()).includes('dock');

        if (hasDock) {
            void scrollCoordinator.scrollToEnd();
            return;
        }

        void scrollCoordinator.scrollToTile(id, { align: 'end' });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: scrollToRightEdgeFor reads a ref; intentional stable dep set.
    useEffect(() => {
        const previous = previousTileOrderRef.current;
        previousTileOrderRef.current = tileOrder;

        if (autoScrollOnChange === 'none') {
            isInitialOrderRef.current = false;
            return;
        }

        if (isInitialOrderRef.current) {
            isInitialOrderRef.current = false;

            if (tileOrder.length > 1) {
                const last = tileOrder[tileOrder.length - 1];

                if (last !== undefined) {
                    scrollToRightEdgeFor(last);
                }
            }

            return;
        }

        const previousSet = new Set(previous);
        const addedIds = tileOrder.filter((id) => !previousSet.has(id));

        if (addedIds.length > 0) {
            const newest = addedIds[addedIds.length - 1];

            if (newest !== undefined) {
                scrollToRightEdgeFor(newest);
            }

            return;
        }

        const currentSet = new Set(tileOrder);
        const removedIds = previous.filter((id) => !currentSet.has(id));

        if (removedIds.length > 0 && tileOrder.length > 0) {
            const last = tileOrder[tileOrder.length - 1];

            if (last !== undefined) {
                scrollToRightEdgeFor(last);
            }
        }
    }, [tileOrder, autoScrollOnChange, scrollCoordinator]);

    const imperativePanels = registeredTiles
        .filter((tile) => tile.kind !== 'dock')
        .map((tile) => (
            <TilingEnginePanel
                key={tile.id}
                id={tile.id}
                maxWidth={tile.maxWidth}
                minWidth={tile.minWidth}
                width={tile.width}
            >
                {tile.content}
            </TilingEnginePanel>
        ));

    const imperativeDocks = registeredTiles
        .filter((tile) => tile.kind === 'dock')
        .map((tile) => (
            <TilingEngineRightDock
                key={tile.id}
                id={tile.id}
                maxWidth={tile.maxWidth}
                minWidth={tile.minWidth}
                width={tile.width}
            >
                {tile.content}
            </TilingEngineRightDock>
        ));

    return (
        <TileWidthProvider value={tileWidths}>
            <TileErrorBoundaryProvider value={tileErrorBoundary ?? null}>
                <motion.div
                    {...containerProps}
                    ref={(node) => {
                        containerRef.current = node;
                        containerProps.ref.current = node;
                    }}
                    className={clsx(
                        'flex flex-row gap-m',
                        'overflow-x-auto overflow-y-hidden',
                        '[scrollbar-width:none]',
                        className,
                    )}
                    style={{
                        cursor: isDragging ? 'grabbing' : undefined,
                        height: `calc(100vh - ${offsetTop}px)`,
                        ...style,
                    }}
                >
                    {mainChild}
                    <AnimatePresence initial={false}>
                        {panelChildren}
                        {imperativePanels}
                    </AnimatePresence>
                    {dockChildren}
                    {imperativeDocks}
                </motion.div>
            </TileErrorBoundaryProvider>
        </TileWidthProvider>
    );
};

const TilingEngineRoot: FunctionComponent<TilingEngineRootProps> = (props) => (
    <TilingEngineProvider>
        <TilingEngineRootInner {...props} />
    </TilingEngineProvider>
);

const TilingEngineSurface = TilingEngineRootInner;

export const TilingEngine = {
    Main: TilingEngineMain,
    Panel: TilingEnginePanel,
    Portal: TilingEnginePortal,
    Provider: TilingEngineProvider,
    RightDock: TilingEngineRightDock,
    Root: TilingEngineRoot,
    Surface: TilingEngineSurface,
} as const;
